import React, { Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { Layout } from '@/components/layout/Layout'
import { ProtectedRoute } from '@/components/layout/ProtectedRoute'
import { LoginPage } from '@/pages/auth/LoginPage'
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage'
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { Spinner } from '@/components/ui/Spinner'
import { ConfirmDialogProvider } from '@/components/ui/ConfirmDialog'
import { Role } from '@/types'

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────
const NotFound    = React.lazy(() => import('./pages/NotFound'))
const Unauthorized = React.lazy(() => import('./pages/Unauthorized'))

// Error pages
const ForbiddenPage   = React.lazy(() => import('./pages/errors/ForbiddenPage').then((m) => ({ default: m.ForbiddenPage })))
const ServerErrorPage = React.lazy(() => import('./pages/errors/ServerErrorPage').then((m) => ({ default: m.ServerErrorPage })))

// SuperAdmin pages
const SuperAdminLayout       = React.lazy(() => import('./pages/superadmin/SuperAdminLayout').then((m) => ({ default: m.SuperAdminLayout })))
const PlatformDashboardPage  = React.lazy(() => import('./pages/superadmin/PlatformDashboardPage').then((m) => ({ default: m.PlatformDashboardPage })))
const TenantsPage            = React.lazy(() => import('./pages/superadmin/TenantsPage').then((m) => ({ default: m.TenantsPage })))
const TenantDetailPage       = React.lazy(() => import('./pages/superadmin/TenantDetailPage').then((m) => ({ default: m.TenantDetailPage })))
const SubscriptionsPage      = React.lazy(() => import('./pages/superadmin/SubscriptionsPage').then((m) => ({ default: m.SubscriptionsPage })))

// Students
const StudentsPage     = React.lazy(() => import('./pages/students/StudentsPage').then(m => ({ default: m.StudentsPage })))
const StudentDetailPage = React.lazy(() => import('./pages/students/StudentDetailPage').then(m => ({ default: m.StudentDetailPage })))

// Groups
const GroupsPage     = React.lazy(() => import('./pages/groups/GroupsPage').then(m => ({ default: m.GroupsPage })))
const GroupDetailPage = React.lazy(() => import('./pages/groups/GroupDetailPage').then(m => ({ default: m.GroupDetailPage })))

// Lessons
const LessonsPage    = React.lazy(() => import('./pages/lessons/LessonsPage').then(m => ({ default: m.LessonsPage })))
const CalendarPage   = React.lazy(() => import('./pages/lessons/CalendarPage').then(m => ({ default: m.CalendarPage })))

// Attendance
const AttendancePage       = React.lazy(() => import('./pages/attendance/AttendancePage').then(m => ({ default: m.AttendancePage })))
const AttendanceReportPage = React.lazy(() => import('./pages/attendance/AttendanceReportPage').then(m => ({ default: m.AttendanceReportPage })))

// Payments
const PaymentsPage      = React.lazy(() => import('./pages/payments/PaymentsPage').then(m => ({ default: m.PaymentsPage })))
const PaymentDetailPage = React.lazy(() => import('./pages/payments/PaymentDetailPage').then(m => ({ default: m.PaymentDetailPage })))

// Messaging
const MessagingPage = React.lazy(() => import('./pages/messaging/MessagingPage').then(m => ({ default: m.MessagingPage })))

// Finance
const FinancePage = React.lazy(() => import('./pages/finance/FinancePage').then(m => ({ default: m.FinancePage })))

// Reports
const ReportsPage         = React.lazy(() => import('./pages/reports/ReportsPage').then(m => ({ default: m.ReportsPage })))
const StudentReportPage   = React.lazy(() => import('./pages/reports/StudentReportPage').then(m => ({ default: m.StudentReportPage })))
const AttendanceReportByFilter = React.lazy(() => import('./pages/reports/AttendanceReportPage').then(m => ({ default: m.AttendanceReportPage })))
const PaymentReportPage      = React.lazy(() => import('./pages/reports/PaymentReportPage').then(m => ({ default: m.PaymentReportPage })))
const FinanceReportPage      = React.lazy(() => import('./pages/reports/FinanceReportPage').then(m => ({ default: m.FinanceReportPage })))
const BirthYearReportPage    = React.lazy(() => import('./pages/reports/BirthYearReportPage').then(m => ({ default: m.BirthYearReportPage })))

// Users & Branches
const UsersPage    = React.lazy(() => import('./pages/users/UsersPage').then(m => ({ default: m.UsersPage })))
const BranchesPage = React.lazy(() => import('./pages/branches/BranchesPage').then(m => ({ default: m.BranchesPage })))

// Settings
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage').then(m => ({ default: m.SettingsPage })))

// Profile
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage').then(m => ({ default: m.ProfilePage })))

// Notifications
const NotificationsPage = React.lazy(() => import('./pages/notifications/NotificationsPage').then(m => ({ default: m.NotificationsPage })))

// ─── Page Fallback ────────────────────────────────────────────────────────────
const PageLoader: React.FC = () => (
  <div className="flex h-[60vh] items-center justify-center">
    <Spinner size="lg" label="Sayfa yükleniyor…" />
  </div>
)

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmDialogProvider>
      <BrowserRouter basename="/app">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public */}
            <Route path="/giris"           element={<LoginPage />} />
            <Route path="/sifremi-unuttum" element={<ForgotPasswordPage />} />
            <Route path="/sifre-sifirla"   element={<ResetPasswordPage />} />

            {/* Protected */}
            <Route
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />

              {/* Öğrenciler */}
              <Route path="/ogrenciler"     element={<StudentsPage />} />
              <Route path="/ogrenciler/:id" element={<StudentDetailPage />} />

              {/* Gruplar */}
              <Route path="/gruplar"     element={<GroupsPage />} />
              <Route path="/gruplar/:id" element={<GroupDetailPage />} />

              {/* Dersler */}
              <Route path="/dersler" element={<LessonsPage />} />

              {/* Yoklama */}
              <Route path="/yoklama"          element={<AttendancePage />} />
              <Route path="/yoklama/rapor"    element={<AttendanceReportPage />} />

              {/* Ödemeler */}
              <Route path="/odemeler"     element={<PaymentsPage />} />
              <Route path="/odemeler/:id" element={<PaymentDetailPage />} />

              {/* Mesajlar */}
              <Route path="/mesajlar" element={<MessagingPage />} />

              {/* Muhasebe */}
              <Route path="/muhasebe" element={<FinancePage />} />

              {/* Raporlar */}
              <Route path="/raporlar"          element={<ReportsPage />} />
              <Route path="/raporlar/ogrenci"  element={<StudentReportPage />} />
              <Route path="/raporlar/yoklama"  element={<AttendanceReportByFilter />} />
              <Route path="/raporlar/odeme"    element={<PaymentReportPage />} />
              <Route path="/raporlar/finans"    element={<FinanceReportPage />} />
              <Route path="/raporlar/dogum-yili" element={<BirthYearReportPage />} />

              {/* Kullanıcılar */}
              <Route path="/kullanicilar" element={<UsersPage />} />

              {/* Şubeler */}
              <Route path="/subeler" element={<BranchesPage />} />

              {/* Ayarlar */}
              <Route path="/ayarlar" element={<SettingsPage />} />

              {/* Eski placeholder rotalar — artık gerçek sayfalara yönlendiriliyor */}
              <Route path="/takvim"      element={<CalendarPage />} />
              <Route path="/profil"      element={<ProfilePage />} />
              <Route path="/bildirimler" element={<NotificationsPage />} />
            </Route>

            {/* SuperAdmin routes (own dark layout) */}
            <Route
              element={
                <ProtectedRoute allowedRoles={[Role.SuperAdmin]}>
                  <SuperAdminLayout />
                </ProtectedRoute>
              }
            >
              <Route path="/superadmin"                    element={<PlatformDashboardPage />} />
              <Route path="/superadmin/tenants"          element={<TenantsPage />} />
              <Route path="/superadmin/tenants/:id"      element={<TenantDetailPage />} />
              <Route path="/superadmin/subscriptions"    element={<SubscriptionsPage />} />
            </Route>

            {/* Error pages */}
            <Route path="/yetkisiz"      element={<Unauthorized />} />
            <Route path="/yasakli"       element={<ForbiddenPage />} />
            <Route path="/sunucu-hatasi" element={<ServerErrorPage />} />
            <Route path="*"              element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      </ConfirmDialogProvider>
    </QueryClientProvider>
  )
}

// ─── Temporary placeholder ────────────────────────────────────────────────────
const ComingSoon: React.FC<{ title: string }> = ({ title }) => (
  <div className="page-container">
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="mb-4 h-14 w-14 rounded-2xl bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-400 text-2xl">
        🚧
      </div>
      <h2 className="text-xl font-bold text-slate-800 dark:text-slate-200">{title}</h2>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">Bu sayfa yapım aşamasında.</p>
    </div>
  </div>
)
